import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import { get, isEmpty } from "lodash";
import FieldError from "components/FieldError";
import visuallyHidden from "theme/visually-hidden";
import formatFieldErrors from "lib/format-field-errors";
import MapsApiWrapper from "components/MapsApiWrapper";

const FrontIconWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  position: absolute;
  height: 29px;
  width: 24px;
`;

const StyledAutoComplete = styled.input`
  border: none;
  background-color: transparent;
  color: ${({ theme }) => theme.themeColors.darkGreen1};
  height: 42px;
  margin-bottom: -1px;
  min-height: 42px;
  padding: 0;
  width: 100%;

  ${({ leadingIcon }) =>
    leadingIcon &&
    css`
      padding-left: 30px;
    `}
  :focus {
    outline: 0;
  }

  ::placeholder {
    color: ${({ theme }) => theme.themeColors.secondary};
  }
`;

const SelectLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize(2)};
  color: ${({ theme }) => theme.themeColors.secondary};
  line-height: 18px;
  position: absolute;
  top: 12px;
  transition: all 0.2s;
  font-weight: 400;

  ${({ showRequiredAsterisk, theme }) =>
    showRequiredAsterisk &&
    css`
      :after {
        content: "*";
        color: ${theme.themeColors.primary};
        font-size: 1rem;
        font-weight: 400;
        line-height: 0.3125rem;
        position: absolute;
        right: -10px;
        top: 50%;
      }
    `}

  ${({ hideLabelForPlaceholder }) =>
    hideLabelForPlaceholder &&
    css`
      ${visuallyHidden};
    `}

  ${({ isActive }) =>
    isActive &&
    css`
      font-size: ${({ theme }) => theme.fontSize(1)};
      transform: translate(0, -25px);
    `}
`;

const InputWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.secondary};
  border-radius: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0;
  height: 42px;
  padding: 0 10px 0 0;
  position: relative;
  width: 100%;

  :hover {
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.secondary};
  }

  :focus-within {
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.primary};
  }
  ${({ error }) =>
    error &&
    css`
      border-color: ${({ theme }) => theme.themeColors.danger};
    `}

  ${({ success }) =>
    success &&
    css`
      border-color: ${({ theme }) => theme.themeColors.success};
    `}

  .pac-target-input {
    &::placeholder {
      color: ${({ theme }) => theme.themeColors.secondary};
      font-size: ${({ theme }) => theme.fontSize(2)};
      margin-bottom: 20px;
    }
  }

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
    `}
`;

const StyledFieldError = styled(FieldError)`
  ${({ isSearch }) =>
    isSearch &&
    css`
      font-size: ${({ theme }) => theme.fontSize(2)};
      bottom: -22px;
      left: 0;
      position: absolute;
    `}
`;

async function handlePlaceSelect(updateQuery, autoKomplete, overrideAddress) {
  const addressObject = overrideAddress || autoKomplete.getPlace();

  const addressComponents = get(addressObject, "address_components", {});

  const zipCode = !isEmpty(addressComponents)
    ? get(
        addressComponents.find((item) => item.types.includes("postal_code")),
        "long_name",
        null
      )
    : null;

  const locality = !isEmpty(addressComponents)
    ? get(
        addressComponents.find((item) => item.types.includes("locality")),
        "long_name",
        null
      )
    : null;

  const region = !isEmpty(addressComponents)
    ? get(
        addressComponents.find((item) =>
          item.types.includes("administrative_area_level_1")
        ),
        "short_name",
        null
      )
    : null;

  const streetNumber = !isEmpty(addressComponents)
    ? get(
        addressComponents.find((item) => item.types.includes("street_number")),
        "long_name",
        null
      )
    : "";

  const streetRoute = !isEmpty(addressComponents)
    ? get(
        addressComponents.find((item) => item.types.includes("route")),
        "short_name",
        null
      )
    : "";

  const shortStreetAddress = `${streetNumber} ${streetRoute}`;

  const addressObjToUse = {
    street_address: shortStreetAddress || "",
    formatted_address: addressObject ? addressObject.formatted_address : "",
    latitude:
      addressObject && addressObject.geometry
        ? addressObject.geometry.location.lat()
        : "",
    longitude:
      addressObject && addressObject.geometry
        ? addressObject.geometry.location.lng()
        : "",
    postal_code: zipCode,
    locality,
    region,
  };

  const query = addressObjToUse;
  updateQuery(query);
}

let hasArrowed = false;
let dirty = false;

function AddressDropdownContent({
  className,
  disabled,
  id,
  input: { onChange, onFocus, name, value },
  isSearch,
  label,
  placeholder,
  LeadingIcon,
  meta: { active, touched, success, submitError, error: syncError } = {},
  showRequiredAsterisk,
}) {
  const [autoKomplete, setAutoKomplete] = useState(null);
  const [service, setService] = useState(null);

  const autoCompleteRef = useRef(null);
  const errors = formatFieldErrors({
    submitError,
    syncError,
  });
  const hasError = touched && errors && errors.length > 0;
  let displayValue;
  let lat;
  let long;
  // Selects the desired address string to display in
  // presentational input, depending on the type of input value.
  if (value.formatted_address) {
    displayValue = value.formatted_address;
    lat = value.latitude;
    long = value.longitude;
  } else if (value.street_address) {
    displayValue = value.street_address;
  } else if (typeof value === "string") {
    displayValue = value;
  } else {
    displayValue = "";
  }
  const firstPrediction = useState({});

  function getService() {
    if (window.google?.maps) {
      const service1 = new window.google.maps.places.AutocompleteService();
      const displaySuggestions = (predictions, status) => {
        if (
          status !== window.google.maps.places.PlacesServiceStatus.OK ||
          !predictions
        ) {
          return;
        }
        firstPrediction.placeId = predictions[0]?.place_id;
      };
      service1.getPlacePredictions(
        { input: value, componentRestrictions: { country: "us" } },
        displaySuggestions
      );
    }
  }

  useEffect(() => {
    if (window.google?.maps) {
      const newAutoKomplete = new window.google.maps.places.Autocomplete(
        autoCompleteRef.current,
        {
          componentRestrictions: { country: "us" },
        }
      );
      setAutoKomplete(newAutoKomplete);
      const map = new window.google.maps.Map(document.createElement("div"));
      const newService = new window.google.maps.places.PlacesService(map);
      setService(newService);
      newAutoKomplete.setFields([
        "address_components",
        "formatted_address",
        "geometry",
      ]);
      newAutoKomplete.addListener("place_changed", () => {
        dirty = false;
        handlePlaceSelect(onChange, newAutoKomplete);
      });
    }
  }, []);

  useEffect(() => {
    const elem = document.getElementById("google-api-autocomplete");
    if (elem && autoKomplete && service) {
      document.getElementById("google-api-autocomplete").onkeydown = (e) => {
        if (e.key === "Enter" && !hasArrowed) {
          hasArrowed = false;
          service.getDetails(firstPrediction, (place) => {
            handlePlaceSelect(onChange, autoKomplete, place);
          });
        } else if (e.key === "Enter" && dirty) {
          e.preventDefault();
        }

        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
          hasArrowed = true;
        }
      };

      document.getElementById("google-api-autocomplete").onkeypress = () => {
        dirty = true;
        hasArrowed = false;
      };

      document.getElementById("google-api-autocomplete").onblur = () => {
        onChange({ formatted_address: displayValue, latitude: lat, longitude:long });
      };
    }
  }, [firstPrediction]);

  useEffect(() => {
    getService();
  }, [displayValue]);

  return (
    <div>
      <InputWrapper
        className={className}
        disabled={disabled}
        error={hasError}
        isSearch={isSearch}
        success={success}
      >
        <SelectLabel
          hideLabelForPlaceholder={!!placeholder}
          isActive={active || value}
          htmlFor={id}
          showRequiredAsterisk={showRequiredAsterisk}
        >
          {label}
        </SelectLabel>

        {LeadingIcon && <FrontIconWrapper>{LeadingIcon}</FrontIconWrapper>}
        <StyledAutoComplete
          disabled={disabled}
          id="google-api-autocomplete"
          onChange={onChange}
          onFocus={onFocus}
          leadingIcon={LeadingIcon}
          placeholder={placeholder}
          ref={autoCompleteRef}
          value={displayValue}
        />

        <input disabled={disabled} name={name} type="hidden" value={value} />
        <StyledFieldError isSearch={isSearch} show={hasError} errors={errors} />
      </InputWrapper>
    </div>
  );
}

AddressDropdownContent.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  input: PropTypes.shape({
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    name: PropTypes.string,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({}),
    ]),
  }),
  isSearch: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  LeadingIcon: PropTypes.node,
  meta: PropTypes.shape({
    error: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.object,
      PropTypes.string,
    ]),
    submitError: PropTypes.bool,
    success: PropTypes.bool,
  }),
  showRequiredAsterisk: PropTypes.bool,
};

AddressDropdownContent.defaultProps = {
  className: "",
  disabled: false,
  id: undefined,
  input: {
    onBlur: () => {},
    onChange: () => {},
    onFocus: () => {},
    name: undefined,
    value: "",
  },
  isSearch: false,
  label: null,
  placeholder: "",
  LeadingIcon: () => {},
  meta: { error: false, success: false, submitError: false },
  showRequiredAsterisk: false,
};

const AddressDropdown = (props) => {
  return (
    <MapsApiWrapper>
      <AddressDropdownContent {...props} />
    </MapsApiWrapper>
  );
};

export default AddressDropdown;
